/**
 * EVC Multiply Dialog Content
 *
 * Uses wagmi-evc hooks for EVC batch transactions.
 * Euler's swap API handles routing internally.
 */

'use client'

import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import type { ActionHandlerState } from '@/features/actions/logic/types'
import { useTrackedMultiplyAction } from '@/features/analytics'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { assets } from '@/ui/assets'
import { DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { BorrowCapDetail } from '@/ui/molecules/borrow-cap-detail/BorrowCapDetail'
import { SupplyCapDetail } from '@/ui/molecules/supply-cap-detail/SupplyCapDetail'
import { ConnectCTAPanel } from '@/ui/organisms/connect-cta-panel/ConnectCTAPanel'
import { usePrivy } from '@privy-io/react-auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Address, parseUnits, zeroAddress } from 'viem'
import { useAccount, useChainId, useConfig, useWriteContract } from 'wagmi'
import { MultiplySuccessView } from './components/MultiplySuccessView'

import { buildPermit2BatchRequest } from '@/domain/euler/evc/buildPermit2BatchRequest'
import { usePermit2Preflight } from '@/domain/euler/evc/usePermit2Preflight'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { type EulerSwapResponse, hasSwapAndVerify, useEulerSwapQuote, useSimulatedBestQuote } from '@/domain/euler/swap'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SwapErrorDisplay } from '@/features/actions/components/SwapErrorDisplay'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { isUserRejection } from '@/lib/errors'
import { getTokenImage } from '@/ui/assets/tokens'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import {
  type BorrowTransactionItem,
  type DepositTransactionItem,
  type EnableCollateralTransactionItem,
  type EnableControllerTransactionItem,
  type SkimTransactionItem,
  type SwapMulticallTransactionItem,
  type SwapVerifierSkimMinTransaction,
  type TransactionItems,
  TransactionLabel,
  type WrapTransactionItem,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'

// =============================================================================
// Types
// =============================================================================

interface EvcMultiplyDialogContentProps {
  closeDialog: () => void
  collateralVault: Address
  collateralSymbol: string
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  liabilityVault: Address
  liabilitySymbol: string
  borrowAmount: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  subAccountId?: number
  wrapCollateralFromNative?: boolean
  multiplier?: number
  /** User-selected slippage for swap operations (percentage value, e.g. 0.5 = 0.5%) */
  slippage?: number
  totalAssets?: bigint
  totalShares?: bigint
  isModifyingExistingPosition: boolean
}

// =============================================================================
// Transaction Builder (wagmi-evc TransactionItems)
// =============================================================================

/**
 * Build multiply transactions using the validated Euler swap path when available:
 *
 * When no swap is needed:
 * 1. Deposit collateral / Wrap native
 * 2. Enable collateral/controller
 * 3. Borrow directly to collateral vault
 * 4. Skim borrowed tokens to the position
 *
 * When Euler Swap API returns a quote that also passes transaction simulation:
 * 1. Deposit collateral / Wrap native
 * 2. Enable collateral/controller
 * 3. Borrow to swapper
 * 4. Swapper.multicall() with swap API data
 * 5. SwapVerifier.verifyAmountMinAndSkim()
 */
function buildMultiplyTransactions({
  collateralVault,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralSymbol,
  liabilityVault,
  liabilityDecimals,
  liabilityAsset,
  liabilitySymbol,
  address,
  subAccountId = 1,
  wrapCollateralFromNative = false,
  eulerSwapQuote,
  borrowAmountBigInt,
}: {
  collateralVault: Address
  collateralAmount: string
  collateralDecimals: number
  collateralAsset: Address
  collateralSymbol: string
  liabilityVault: Address
  liabilityDecimals: number
  liabilityAsset: Address
  liabilitySymbol: string
  address: Address
  subAccountId?: number
  wrapCollateralFromNative?: boolean
  eulerSwapQuote?: EulerSwapResponse | null
  borrowAmountBigInt: bigint
}): TransactionItems {
  // Validate address before proceeding
  if (!address || address === zeroAddress) {
    return []
  }

  // Ensure address is a valid hex string
  if (typeof address !== 'string' || !address.startsWith('0x')) {
    console.error('[buildMultiplyTransactions] Invalid address format:', address)
    return []
  }

  const position = getSubAccountAddress(address, subAccountId)
  const collateralAmountBigInt = parseUnits(collateralAmount || '0', collateralDecimals)

  if (collateralAmountBigInt === 0n && borrowAmountBigInt === 0n) {
    return []
  }

  const transactions: TransactionItems = []

  // 1. If wrapping native, wrap WETH first
  if (wrapCollateralFromNative && collateralAmountBigInt > 0n) {
    const wrapTransaction: WrapTransactionItem = {
      label: TransactionLabel.WRAP,
      vault: collateralVault,
      asset: {
        address: collateralAsset,
        name: collateralSymbol,
        symbol: collateralSymbol,
        decimals: collateralDecimals,
      },
      from: address,
      to: position,
      value: collateralAmountBigInt.toString(),
      totalAssets: '0',
      totalShares: '0',
    }
    transactions.push(wrapTransaction)
  }

  // 2. Enable collateral vault
  if (collateralAmountBigInt > 0n) {
    const enableCollateral: EnableCollateralTransactionItem = {
      label: TransactionLabel.ENABLE_COLLATERAL,
      vault: collateralVault,
      asset: {
        address: collateralAsset,
        name: collateralSymbol,
        symbol: collateralSymbol,
        decimals: collateralDecimals,
      },
      from: position,
      to: position,
    }
    transactions.push(enableCollateral)
  }

  // 3. Deposit collateral (only if NOT wrapping)
  // Uses vault.deposit which internally pulls via Permit2 (spender=vault, not EVC)
  if (collateralAmountBigInt > 0n && !wrapCollateralFromNative) {
    const depositCollateral: DepositTransactionItem = {
      label: TransactionLabel.DEPOSIT,
      vault: collateralVault,
      asset: {
        address: collateralAsset,
        name: collateralSymbol,
        symbol: collateralSymbol,
        decimals: collateralDecimals,
      },
      from: address,
      to: position,
      value: collateralAmountBigInt.toString(),
    }
    transactions.push(depositCollateral)
  }

  // 4. Enable controller for liability vault
  if (borrowAmountBigInt > 0n) {
    const enableController: EnableControllerTransactionItem = {
      label: TransactionLabel.ENABLE_CONTROLLER,
      vault: liabilityVault,
      asset: {
        address: liabilityAsset,
        name: liabilitySymbol,
        symbol: liabilitySymbol,
        decimals: liabilityDecimals,
      },
      from: position,
      to: position,
    }
    transactions.push(enableController)
  }

  // 5. Borrow and Swap (for multiply)
  if (borrowAmountBigInt > 0n) {
    const needsSwap = collateralAsset.toLowerCase() !== liabilityAsset.toLowerCase()

    const collateralAssetInfo = {
      address: collateralAsset,
      name: collateralSymbol,
      symbol: collateralSymbol,
      decimals: collateralDecimals,
    }

    const liabilityAssetInfo = {
      address: liabilityAsset,
      name: liabilitySymbol,
      symbol: liabilitySymbol,
      decimals: liabilityDecimals,
    }

    if (!needsSwap) {
      // Same token: borrow directly to vault and skim
      const borrowTransaction: BorrowTransactionItem = {
        label: TransactionLabel.BORROW,
        vault: liabilityVault,
        asset: liabilityAssetInfo,
        from: position,
        to: collateralVault,
        value: borrowAmountBigInt.toString(),
      }
      transactions.push(borrowTransaction)

      const skimBorrowed: SkimTransactionItem = {
        label: TransactionLabel.SKIM,
        vault: collateralVault,
        asset: collateralAssetInfo,
        from: position,
        to: position,
        value: borrowAmountBigInt.toString(),
        isMax: true,
      }
      transactions.push(skimBorrowed)
    } else if (hasSwapAndVerify(eulerSwapQuote)) {
      // Different tokens: Borrow → Swapper → Collateral Vault → SwapVerifier
      //
      // Flow:
      // 1. Borrow liability tokens directly TO THE SWAPPER (Swapper receives borrowed tokens)
      // 2. Swapper.multicall() executes swap, outputs to collateral vault
      // 3. SwapVerifier.verifyAmountMinAndSkim() enforces slippage + mints shares to position
      //
      // This avoids the issue where Swapper tries to withdraw from a vault
      // when we have debt (not shares) in that vault.

      const swap = eulerSwapQuote.swap
      const verify = eulerSwapQuote.verify

      const borrowToSwapper: BorrowTransactionItem = {
        label: TransactionLabel.BORROW,
        vault: liabilityVault,
        asset: liabilityAssetInfo,
        from: position,
        to: swap.swapperAddress,
        value: eulerSwapQuote.amountIn,
      }
      transactions.push(borrowToSwapper)

      const calls = swap.multicallItems.map((item) => item.data)

      const swapTx: SwapMulticallTransactionItem = {
        label: TransactionLabel.SWAP_MULTICALL,
        from: position,
        to: collateralVault,
        asset: liabilityAssetInfo,
        fromVault: zeroAddress as Address,
        toVault: collateralVault,
        accountTo: position,
        receiver: collateralVault,
        assetIn: liabilityAssetInfo,
        assetOut: collateralAssetInfo,
        amountIn: eulerSwapQuote.amountIn,
        amountOut: eulerSwapQuote.amountOutMin,
        swapperAddress: swap.swapperAddress,
        swapperData: swap.swapperData,
        calls,
      }
      transactions.push(swapTx)

      const swapVerify: SwapVerifierSkimMinTransaction = {
        label: TransactionLabel.SWAP_VERIFIER_SKIM_MIN,
        from: position,
        to: position,
        asset: collateralAssetInfo,
        vault: verify.vault,
        account: verify.account,
        amount: verify.amount,
        deadline: verify.deadline.toString(),
        verifierAddress: verify.verifierAddress,
        verifierData: verify.verifierData,
      }
      transactions.push(swapVerify)
    }
  }

  return transactions
}

// =============================================================================
// Inner Component (uses wagmi hooks)
// =============================================================================

function EvcMultiplyDialogContentInner({
  closeDialog,
  collateralVault,
  collateralSymbol,
  collateralAmount,
  collateralDecimals,
  collateralAsset,
  collateralLogoURI,
  liabilityVault,
  liabilitySymbol,
  borrowAmount,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  subAccountId = 1,
  wrapCollateralFromNative,
  multiplier,
  slippage = 0.5,
  isModifyingExistingPosition,
}: EvcMultiplyDialogContentProps) {
  const { address } = useAccount()
  const chainId = useChainId()
  const { connectWallet } = usePrivy()
  const addSubaccount = useSubaccountStore((state) => state.addSubaccount)

  const position = useMemo(() => {
    if (!address || address === zeroAddress) return zeroAddress
    return getSubAccountAddress(address, subAccountId)
  }, [address, subAccountId])

  const borrowAmountBigInt = useMemo(
    () => parseUnits(borrowAmount || '0', liabilityDecimals),
    [borrowAmount, liabilityDecimals],
  )

  const needsSwap = useMemo(
    () => Boolean(collateralAsset && liabilityAsset && collateralAsset.toLowerCase() !== liabilityAsset.toLowerCase()),
    [collateralAsset, liabilityAsset],
  )

  const collateralAmountBigInt = useMemo(
    () => parseUnits(collateralAmount || '0', collateralDecimals),
    [collateralAmount, collateralDecimals],
  )

  const swapQueryEnabled = Boolean(
    needsSwap && address && address !== zeroAddress && position !== zeroAddress && borrowAmountBigInt > 0n,
  )

  const hasValidSwapAddresses = Boolean(
    collateralAsset && collateralAsset !== zeroAddress && liabilityAsset && liabilityAsset !== zeroAddress,
  )

  // Get Euler swap quote for multiply
  const {
    quote: eulerSwapQuote,
    quotes: allQuotes,
    isLoading: isEulerQuoteLoading,
    isRefetching: isQuoteRefetching,
    error: eulerQuoteError,
    refetch: refetchQuotes,
  } = useEulerSwapQuote({
    tokenIn: needsSwap && hasValidSwapAddresses ? liabilityAsset : undefined,
    tokenOut: needsSwap && hasValidSwapAddresses ? collateralAsset : undefined,
    amount: needsSwap && hasValidSwapAddresses ? borrowAmountBigInt.toString() : undefined,
    vaultIn: needsSwap && hasValidSwapAddresses ? zeroAddress : undefined,
    receiver: needsSwap && hasValidSwapAddresses ? collateralVault : undefined,
    accountIn: needsSwap && hasValidSwapAddresses ? position : undefined,
    accountOut: needsSwap && hasValidSwapAddresses ? position : undefined,
    slippage,
    enabled: swapQueryEnabled && hasValidSwapAddresses,
  })

  // Build transactions for a given quote (used by useSimulatedBestQuote)
  const buildTransactionsForQuote = useCallback(
    (quote: EulerSwapResponse): TransactionItems => {
      if (!address || address === zeroAddress) return []
      if (!collateralVault || !collateralAsset || !liabilityVault || !liabilityAsset) return []

      return buildMultiplyTransactions({
        collateralVault,
        collateralAmount,
        collateralDecimals,
        collateralAsset,
        collateralSymbol,
        liabilityVault,
        liabilityDecimals,
        liabilityAsset,
        liabilitySymbol,
        address,
        subAccountId,
        wrapCollateralFromNative,
        eulerSwapQuote: quote,
        borrowAmountBigInt,
      })
    },
    [
      address,
      collateralVault,
      collateralAmount,
      collateralDecimals,
      collateralAsset,
      collateralSymbol,
      liabilityVault,
      liabilityDecimals,
      liabilityAsset,
      liabilitySymbol,
      subAccountId,
      wrapCollateralFromNative,
      borrowAmountBigInt,
    ],
  )

  // Simulate all quotes in parallel and pick the first passing one
  const {
    quote: validatedQuote,
    transactions: validatedTransactions,
    isSimulating: isValidatingQuotes,
    error: validationError,
    refetch: refetchSimulation,
  } = useSimulatedBestQuote({
    quotes: allQuotes,
    buildTransactions: buildTransactionsForQuote,
    queryKeyDeps: [position, collateralVault, liabilityVault, borrowAmountBigInt.toString(), collateralAmount],
    enabled: allQuotes.length > 0 && !!address && address !== zeroAddress,
  })

  const handleRefetchQuotes = useCallback(() => {
    refetchQuotes()
    refetchSimulation()
  }, [refetchQuotes, refetchSimulation])

  // Use the validated quote for display. Don't fall back to the unvalidated API quote
  // when validation failed — showing details of a quote proven to revert is misleading.
  const displayQuote = validatedQuote ?? (validationError ? null : eulerSwapQuote)

  const isQuoteLoading = isEulerQuoteLoading || isValidatingQuotes
  const quoteError = validationError || eulerQuoteError
  const hasSwapQuote = !!displayQuote
  const swapSource = displayQuote
    ? `EulerSwap${displayQuote.route?.length ? ` (${displayQuote.route.map((r: { providerName?: string }) => r.providerName).join(' → ')})` : ''}`
    : null

  const permit2 = usePermit2Preflight({
    token: wrapCollateralFromNative
      ? undefined
      : new Token({
          symbol: TokenSymbol(collateralSymbol),
          name: collateralSymbol,
          decimals: collateralDecimals,
          address: CheckedAddress(collateralAsset),
          unitPriceUsd: '0',
          ...(collateralLogoURI && { logoURI: collateralLogoURI }),
        }),
    amount: wrapCollateralFromNative ? undefined : collateralAmountBigInt,
    spender: wrapCollateralFromNative ? undefined : (collateralVault as Address),
    enabled: !!address && !wrapCollateralFromNative && collateralAmountBigInt > 0n,
  })

  const config = useConfig()
  // Skip Permit2 when using native collateral or when no additional collateral is being deposited.
  const skipPermit2 = wrapCollateralFromNative || collateralAmountBigInt === 0n
  // Only simulate when permit2 is ready (for ERC20) or for native collateral (no permit needed)
  const canSimulate =
    !!address &&
    validatedTransactions.length > 0 &&
    !!validatedQuote &&
    !isEulerQuoteLoading &&
    (skipPermit2 || permit2.status === 'ready')

  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions: validatedTransactions,
    query: {
      enabled: canSimulate,
      retry: false,
    },
  })

  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })
  const { markSubmitted } = useTrackedMultiplyAction({
    marketType: 'eborrow',
    collateralToken: collateralSymbol,
    collateralMarket: collateralVault,
    collateralAmount: collateralAmount || '0',
    liabilityToken: liabilitySymbol,
    liabilityMarket: liabilityVault,
    borrowAmount: borrowAmount || '0',
    multiplier,
    txHash,
    isConfirmed,
    error: writeError || waitError || simulationError || permit2.error || quoteError,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) {
      console.error('[EvcMultiplyDialogContent] Simulation not ready')
      return
    }

    try {
      // For native collateral or borrow-only-on-existing-position, use simulation request directly.
      if (skipPermit2) {
        markSubmitted()
        writeContract(simulationData.request as any)
        return
      }

      const executionRequest = buildPermit2BatchRequest({
        simulationData,
        permitTx: permit2.status === 'ready' ? permit2.permitTx : null,
        chainId,
      })
      markSubmitted()
      writeContract(executionRequest as any)
    } catch (err) {
      console.error('[EvcMultiplyDialogContent] Execute error:', err)
    }
  }, [simulationData, writeContract, skipPermit2, permit2.permitTx, permit2.status, chainId, address, markSubmitted])

  // ========== ACTION TIMEOUT TRACKING ==========
  // Track timeout for multiply execution to provide feedback when operations hang
  const multiplyTimeout = useActionTimeout({
    timeoutMs: 15000, // 15 seconds for multiply operations
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError || permit2.error || quoteError,
  })

  // Wrapped execute handler that tracks timeout
  const handleExecuteWithTimeout = useCallback(() => {
    multiplyTimeout.handleActionClick(handleExecute)
  }, [multiplyTimeout, handleExecute])

  // Determine action handler states for the grid
  const collateralToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(collateralSymbol),
        name: collateralSymbol,
        decimals: collateralDecimals,
        address: CheckedAddress(collateralAsset),
        unitPriceUsd: '0',
        ...(collateralLogoURI && { logoURI: collateralLogoURI }),
      }),
    [collateralSymbol, collateralDecimals, collateralAsset, collateralLogoURI],
  )

  const liabilityToken = useMemo(
    () =>
      new Token({
        symbol: TokenSymbol(liabilitySymbol),
        name: liabilitySymbol,
        decimals: liabilityDecimals,
        address: CheckedAddress(liabilityAsset),
        unitPriceUsd: '0',
        ...(liabilityLogoURI && { logoURI: liabilityLogoURI }),
      }),
    [liabilitySymbol, liabilityDecimals, liabilityAsset, liabilityLogoURI],
  )

  const approveStatus: ActionHandlerState['status'] = useMemo(() => {
    if (skipPermit2) return 'success' // Skip Permit2 for native deposits or zero-collateral adjustments
    if (permit2.status === 'idle') return 'disabled'
    if (permit2.status === 'needs_approval') return 'ready'
    if (permit2.status === 'approving' || permit2.isApproving) return 'loading'
    return 'success'
  }, [permit2.status, permit2.isApproving, skipPermit2])

  const signStatus: ActionHandlerState['status'] = useMemo(() => {
    if (skipPermit2) return 'success' // Skip Permit2 for native deposits or zero-collateral adjustments
    if (approveStatus !== 'success') return 'disabled'
    if (permit2.status === 'needs_signature') return 'ready'
    if (permit2.status === 'signing' || permit2.isSigning) return 'loading'
    if (permit2.status === 'ready') return 'success'
    return 'disabled'
  }, [approveStatus, permit2.status, permit2.isSigning, skipPermit2])

  const executeStatus: ActionHandlerState['status'] = useMemo(() => {
    // Active transaction states take priority - never allow retry while tx is pending
    if (isWriting || isWaiting) return 'loading'
    if (isConfirmed) return 'success'

    // Only disable for non-user-rejection errors (allow retry after wallet rejection)
    const hasBlockingError =
      (simulationError && !isUserRejection(simulationError)) ||
      (writeError && !isUserRejection(writeError)) ||
      (waitError && !isUserRejection(waitError)) ||
      quoteError

    // Check prerequisites - must be met even after timeout
    if (hasBlockingError) return 'disabled'

    if (skipPermit2) {
      // Native collateral or zero-collateral adjustments: need simulation data to execute
      if (!simulationData?.request) {
        return isSimulating || isQuoteLoading ? 'loading' : 'disabled'
      }
      return 'ready'
    }

    // ERC20 collateral: need permit2 flow + simulation data
    if (signStatus !== 'success') return 'disabled'
    if (!simulationData?.request) {
      return isSimulating || isQuoteLoading ? 'loading' : 'disabled'
    }
    return 'ready'
  }, [
    skipPermit2,
    signStatus,
    isSimulating,
    isQuoteLoading,
    isWriting,
    isWaiting,
    isConfirmed,
    simulationError,
    writeError,
    waitError,
    quoteError,
    simulationData?.request,
  ])

  // Track success state
  const [isSuccess, setIsSuccess] = useState(false)
  useEffect(() => {
    if (isConfirmed && !isSuccess) {
      setIsSuccess(true)

      // Optimistic update: add subaccount to global cache
      // (multiply creates a new borrow position with enableController)
      addSubaccount(subAccountId)
    }
  }, [isConfirmed, isSuccess, subAccountId, addSubaccount])

  // Filter out user rejection errors for display
  const displayError = useMemo(() => {
    // Timeout error takes priority for better UX feedback
    if (multiplyTimeout.isTimedOut) {
      return multiplyTimeout.displayError
    }
    if (isUserRejection(simulationError as Error | null)) return null
    if (isUserRejection(writeError as Error | null)) return null
    if (isUserRejection(waitError as Error | null)) return null
    return simulationError || writeError || waitError || quoteError
  }, [simulationError, writeError, waitError, quoteError, multiplyTimeout.isTimedOut, multiplyTimeout.displayError])

  // ============================================================================
  // NOW we can do early returns after all hooks are called
  // ============================================================================

  // Define these before early returns to avoid linting errors
  const collateralImage = getTokenImage(TokenSymbol(collateralSymbol))
  const liabilityImage = getTokenImage(TokenSymbol(liabilitySymbol))
  const hasCollateral = collateralAmount && Number(collateralAmount) > 0
  const hasBorrow = borrowAmount && Number(borrowAmount) > 0

  if (!address) {
    const icons = assets.walletIcons
    const WALLET_ICONS_PATHS = [icons.metamask, icons.walletConnect, icons.coinbase]
    return (
      <MultiPanelDialog>
        <DialogTitle>Open Multiply Position</DialogTitle>

        <DialogPanel>
          <div className="flex items-center justify-center py-8">
            <ConnectCTAPanel
              header="Connect your wallet to open a multiply position"
              iconPaths={WALLET_ICONS_PATHS}
              action={connectWallet}
              buttonText="Connect wallet"
            />
          </div>
        </DialogPanel>
      </MultiPanelDialog>
    )
  }

  // Handle success
  if (isSuccess) {
    return (
      <MultiplySuccessView
        collateralToken={collateralToken}
        collateralAmount={NormalizedUnitNumber(collateralAmount || '0')}
        borrowToken={liabilityToken}
        borrowAmount={NormalizedUnitNumber(borrowAmount || '0')}
        multiplier={multiplier}
        onProceed={closeDialog}
        proceedText="Done"
      />
    )
  }

  return (
    <MultiPanelDialog>
      <DialogTitle>{isModifyingExistingPosition ? 'Adjust Multiply Position' : 'Open Multiply Position'}</DialogTitle>

      <DialogPanel>
        {/* Position Summary */}
        <div className="flex flex-col gap-4">
          {/* Collateral Deposit */}
          {hasCollateral && (
            <div className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-3">
                {collateralImage && (
                  <TokenImage
                    src={collateralImage}
                    alt={collateralSymbol}
                    width={32}
                    height={32}
                    className="rounded-full"
                    fallbackSrc={collateralLogoURI}
                    tokenAddress={collateralAsset}
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">
                    {wrapCollateralFromNative ? 'Deposit (Native)' : 'Deposit Collateral'}
                  </span>
                  <span className="font-medium text-sm text-white">{collateralSymbol}</span>
                </div>
              </div>
              <span className="font-semibold text-lg text-white">
                {Number(collateralAmount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
            </div>
          )}

          {/* Borrow & Reinvest */}
          {hasBorrow && (
            <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3">
                {liabilityImage && (
                  <TokenImage
                    src={liabilityImage}
                    alt={liabilitySymbol}
                    width={32}
                    height={32}
                    className="rounded-full"
                    fallbackSrc={liabilityLogoURI}
                    tokenAddress={liabilityAsset}
                  />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Borrow & Reinvest</span>
                  <span className="font-medium text-sm text-white">{liabilitySymbol}</span>
                </div>
              </div>
              <span className="font-semibold text-lg text-primary">
                {Number(borrowAmount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6,
                })}
              </span>
            </div>
          )}
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          {multiplier && (
            <TransactionOverviewDetailsItem label="Multiplier">{multiplier.toFixed(2)}x</TransactionOverviewDetailsItem>
          )}
          {hasCollateral && (
            <TransactionOverviewDetailsItem label="Initial collateral">
              {Number(collateralAmount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {collateralSymbol}
            </TransactionOverviewDetailsItem>
          )}
          {hasBorrow && hasCollateral && (
            <TransactionOverviewDetailsItem label="Total exposure">
              {(Number(collateralAmount) + Number(borrowAmount)).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {collateralSymbol}
            </TransactionOverviewDetailsItem>
          )}
          {hasBorrow && (
            <TransactionOverviewDetailsItem label="Debt">
              {Number(borrowAmount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 6,
              })}{' '}
              {liabilitySymbol}
            </TransactionOverviewDetailsItem>
          )}
          {hasBorrow && (
            <BorrowCapDetail vaultAddress={liabilityVault} decimals={liabilityDecimals} symbol={liabilitySymbol} />
          )}
          {hasCollateral && (
            <SupplyCapDetail vaultAddress={collateralVault} decimals={collateralDecimals} symbol={collateralSymbol} />
          )}
          {/* Swap via - always show when swap is needed */}
          {needsSwap && (
            <TransactionOverviewDetailsItem label="Swap via">
              {isQuoteLoading ? (
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : hasSwapQuote && swapSource ? (
                <span className="text-green-400">{swapSource}</span>
              ) : quoteError ? (
                <span className="text-red-400">Unavailable</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TransactionOverviewDetailsItem>
          )}
        </div>

        {/* Simulation Status */}
        {isSimulating && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Simulating transaction...
          </div>
        )}

        {/* Swap Quote Loading Status */}
        {needsSwap && isQuoteLoading && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Fetching swap quote...
          </div>
        )}

        {/* Error display — covers swap errors (quote-not-found as yellow, simulation as red)
            AND non-swap errors (write / wait failures) that can occur even when needsSwap is false. */}
        {!multiplyTimeout.isTimedOut && (
          <SwapErrorDisplay
            error={displayError as Error | null}
            isLoading={isQuoteLoading}
            onRefetch={handleRefetchQuotes}
            isRefetching={isQuoteRefetching || isValidatingQuotes}
            className="mt-4"
          />
        )}

        {/* Timeout error display - shows when operation takes too long */}
        <TimeoutErrorDisplay
          isTimedOut={multiplyTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid (matching borrow dialog) */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          {/* Step 1: Approve Permit2 (skip for native) */}
          {!skipPermit2 && (
            <ActionRow index={1}>
              <ActionRow.Icon path={assets.actions.approve} actionStatus={approveStatus} />
              <ActionRow.Title
                icon={<TokenIcon token={collateralToken} className="h-6" />}
                actionStatus={approveStatus}
              >
                Approve Permit2
              </ActionRow.Title>
              <ActionRow.Description successMessage="Approved!" actionStatus={approveStatus} variant="compact" />
              <ActionRow.Action onAction={() => permit2.approvePermit2?.()} status={approveStatus}>
                Approve
              </ActionRow.Action>
            </ActionRow>
          )}

          {/* Step 2: Sign Permit (skip for native) */}
          {!skipPermit2 && (
            <ActionRow index={2}>
              <ActionRow.Icon path={assets.actions.approve} actionStatus={signStatus} />
              <ActionRow.Title icon={<TokenIcon token={collateralToken} className="h-6" />} actionStatus={signStatus}>
                Sign Permit
              </ActionRow.Title>
              <ActionRow.Description successMessage="Signed!" actionStatus={signStatus} variant="compact" />
              <ActionRow.Action onAction={() => permit2.signPermit2?.()} status={signStatus}>
                Sign
              </ActionRow.Action>
            </ActionRow>
          )}

          {/* Step 3: Open Position */}
          <ActionRow index={skipPermit2 ? 1 : 3}>
            <ActionRow.Icon path={assets.actions.borrow} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={liabilityToken} className="h-6" />} actionStatus={executeStatus}>
              Open Position
            </ActionRow.Title>
            <ActionRow.Description successMessage="Position Opened!" actionStatus={executeStatus} variant="compact" />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={executeStatus}>
              {multiplyTimeout.isTimedOut ? 'Retry' : isSimulating || isQuoteLoading ? 'Preparing...' : 'Open Position'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}

// =============================================================================
// Exported Component
// =============================================================================

export function EvcMultiplyDialogContent(props: EvcMultiplyDialogContentProps) {
  return <EvcMultiplyDialogContentInner {...props} />
}
